/**
 * Business Hours Display Component
 * 영업시간 표시 컴포넌트
 */

import { Clock } from "lucide-react";

interface BusinessHoursDisplayProps {
  hours: any; // JSON object or string
}

export function BusinessHoursDisplay({ hours }: BusinessHoursDisplayProps) {
  if (!hours) return null;

  let parsedHours: Record<string, string> = {};

  // Parse JSON string if needed
  if (typeof hours === "string") {
    try {
      parsedHours = JSON.parse(hours);
    } catch (e) {
      // If parsing fails, treat as plain text
      return (
        <div className="flex items-start gap-2 text-sm">
          <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <span className="text-muted-foreground">{hours}</span>
        </div>
      );
    }
  } else {
    parsedHours = hours;
  }

  if (Array.isArray(parsedHours)) {
    return (
      <div className="space-y-2">
        {parsedHours.filter(Boolean).map((line, index) => (
          <div key={`${String(line)}-${index}`} className="flex items-start gap-2 border-b py-1 last:border-0">
            <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{String(line)}</span>
          </div>
        ))}
      </div>
    );
  }

  const dayNames: Record<string, string> = {
    monday: "월요일",
    tuesday: "화요일",
    wednesday: "수요일",
    thursday: "목요일",
    friday: "금요일",
    saturday: "토요일",
    sunday: "일요일",
    weekdays: "평일",
    weekend: "주말",
  };

  const formatHours = (dayData: any): string => {
    if (!dayData) return "휴무";

    // 객체 형태인 경우 (open, close, closed 필드)
    if (typeof dayData === "object") {
      if (dayData.closed === true || dayData.closed === 1) return "휴무";
      if (dayData.open && dayData.close) {
        return `${dayData.open} - ${dayData.close}`;
      }
      return "시간 미정";
    }

    // 문자열 형태인 경우
    const hoursStr = String(dayData);
    const lower = hoursStr.toLowerCase();
    if (lower === "closed" || lower === "휴무" || lower === "close") return "휴무";
    return hoursStr;
  };

  return (
    <div className="space-y-2">
      {Object.entries(parsedHours).map(([day, dayData]) => {
        const dayName = dayNames[day.toLowerCase()] || day;
        const hours = formatHours(dayData);

        return (
          <div key={day} className="flex justify-between items-center py-1 border-b last:border-0">
            <span className="text-muted-foreground font-medium">
              {dayName}
            </span>
            <span className="text-foreground font-semibold">{hours}</span>
          </div>
        );
      })}
    </div>
  );
}
